import React, { useEffect, useMemo, useState } from "react";
import QRCode from "react-qr-code";
import logo from "../../Assets/Logos/ashok1.png";
import Front from "../../Assets/Logos/Front.png";
import Back from "../../Assets/Logos/Back.png";
import { stateCodesMap } from "../../utils/stateCodes";
import { useAssets } from "../../Context/AssetsContext";

// ✅ CLOUDINARY DIRECT URLs
const CLOUDINARY_IMAGES = {
  chip: "https://res.cloudinary.com/dfjeuiwro/image/upload/v1769506720/CHIP-RC_zxcump.png",
  signatures: [
    "https://res.cloudinary.com/dfjeuiwro/image/upload/v1769506654/RC-IMG1_oenwfq.png",
    "https://res.cloudinary.com/dfjeuiwro/image/upload/v1769506674/RC-IMG2_pvpyao.png",
    "https://res.cloudinary.com/dfjeuiwro/image/upload/v1769506688/RC-IMG3_ruttdo.png",
    "https://res.cloudinary.com/dfjeuiwro/image/upload/v1771504606/WhatsApp_Image_2026-02-19_at_5.35.36_PM-removebg-preview_soybor.png",
    "https://res.cloudinary.com/dfjeuiwro/image/upload/v1771504616/WhatsApp_Image_2026-02-19_at_5.39.09_PM-removebg-preview_qd1fgf.png",
    "https://res.cloudinary.com/dfjeuiwro/image/upload/v1771504623/WhatsApp_Image_2026-02-19_at_5.41.25_PM-removebg-preview_dhye6c.png",
  ],
};

// ✅ safeString: converts ANY value (object, array, null, undefined) to a safe renderable string
const safeString = (value, fieldName = "unknown") => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (value instanceof Date) return value.toLocaleString();
  if (Array.isArray(value)) {
    console.warn(
      `⚠️ [SmartRC] Array field "${fieldName}":`,
      JSON.stringify(value),
    );
    return (
      value
        .map((v) => safeString(v))
        .filter(Boolean)
        .join(", ") || ""
    );
  }
  if (typeof value === "object") {
    console.error(
      `❌ [SmartRC] Object field "${fieldName}":`,
      JSON.stringify(value),
    );
    if (value.name) return String(value.name);
    if (value.value) return String(value.value);
    if (value.label) return String(value.label);
    if (value.code) return String(value.code);
    if (value.text) return String(value.text);
    // ✅ CRITICAL FIX: Return "" so "{}" never renders on screen
    return "";
  }
  return String(value);
};

const getBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.setAttribute("crossOrigin", "anonymous");
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = (error) => reject(error);
    img.src = url;
  });
};

const OdishaRCCard = ({
  data,
  includeSignature = false,
  includeChip = false,
  signatureUrl = null,
  downloadCount = 0,
}) => {
  const { assetsReady, assets } = useAssets();
  const [compressedFront, setCompressedFront] = useState("");
  const [compressedBack, setCompressedBack] = useState("");
  const [signatureImage, setSignatureImage] = useState(null);
  const [chipImage, setChipImage] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // ✅ Log all incoming data fields on mount/update for debugging
  useEffect(() => {
    if (!data) return;
    console.group(
      `🔍 [SmartRC] Data field type audit for VRN: ${safeString(data?.rc_number, "rc_number")}`,
    );
    Object.entries(data).forEach(([key, val]) => {
      const type = Array.isArray(val) ? "array" : typeof val;
      const isProblematic = type === "object" && val !== null;
      console.log(
        `  ${isProblematic ? "❌" : "✅"} ${key}: [${type}] = ${isProblematic ? JSON.stringify(val) : val}`,
      );
    });
    console.groupEnd();
  }, [data]);

  const stateName = useMemo(() => {
    const stateCode = safeString(data?.rc_number, "rc_number")
      .trim()
      .slice(0, 2)
      .toUpperCase();
    return stateCodesMap[stateCode] || "";
  }, [data?.rc_number]);

  useEffect(() => {
    if (includeSignature && signatureUrl) {
      const loadSignature = async () => {
        try {
          const base64 = await getBase64(signatureUrl);
          setSignatureImage(base64);
          console.log(`✅ [SmartRC] Loaded signature from: ${signatureUrl}`);
        } catch (err) {
          console.error("❌ [SmartRC] Error loading signature:", err);
          setSignatureImage(null);
        }
      };
      loadSignature();
    } else {
      setSignatureImage(null);
    }
  }, [signatureUrl, includeSignature]);

  useEffect(() => {
    if (includeChip && CLOUDINARY_IMAGES.chip) {
      const loadChip = async () => {
        try {
          const base64 = await getBase64(CLOUDINARY_IMAGES.chip);
          setChipImage(base64);
          console.log(`✅ [SmartRC] Loaded chip`);
        } catch (err) {
          console.error("❌ [SmartRC] Error loading chip:", err);
          setChipImage(null);
        }
      };
      loadChip();
    } else {
      setChipImage(null);
    }
  }, [includeChip]);

  useEffect(() => {
    const checkIfMobile = () => setIsMobile(window.innerWidth < 768);
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  useEffect(() => {
    if (assetsReady && assets.rcFront && assets.rcBack) {
      setCompressedFront(assets.rcFront);
      setCompressedBack(assets.rcBack);
    } else {
      const compressImage = (src, callback) => {
        const img = new Image();
        img.src = src;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          const maxWidth = isMobile ? 300 : 500;
          const ratio = maxWidth / img.width;
          canvas.width = maxWidth;
          canvas.height = img.height * ratio;
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          callback(canvas.toDataURL("image/png", 0.7));
        };
      };
      compressImage(Front, setCompressedFront);
      compressImage(Back, setCompressedBack);
    }
  }, [isMobile, assetsReady, assets]);

  const generateStaticQRData = () => {
    const lines = [
      `State Name: ${stateName || ""}`,
      `Office Name: ${safeString(data?.office_name, "office_name")}`,
      `Vehicle Registration Number: ${safeString(data?.rc_number, "rc_number")}`,
      `Chassis Number: ${safeString(data?.vehicle_chasi_number, "vehicle_chasi_number")}`,
      `Engine Number: ${safeString(data?.vehicle_engine_number, "vehicle_engine_number")}`,
      `Seating Capacity: ${safeString(data?.seating_capacity, "seating_capacity")}`,
      `Standing Capacity: ${safeString(data?.standing_capacity, "standing_capacity")}`,
      `Sleeper Capacity: ${safeString(data?.sleeper_capacity, "sleeper_capacity")}`,
      `Owner Name: ${safeString(data?.owner_name, "owner_name")}`,
      `Laden Weight: ${safeString(data?.vehicle_gross_weight, "vehicle_gross_weight")}`,
      `Unladen Weight: ${safeString(data?.vehicle_unladen_weight, "vehicle_unladen_weight")}`,
      `Father's Name: ${safeString(data?.father_name, "father_name")}`,
      `Registration From Date: ${safeString(data?.registration_date, "registration_date")}`,
      `Registration Upto Date: ${safeString(data?.fit_up_to, "fit_up_to")}`,
      `Address: ${safeString(data?.present_address, "present_address")}`,
    ];
    return lines.join(" | ");
  };

  const qrDataText = generateStaticQRData();

  const CircleBadge = ({
    text,
    bgColor,
    textColor = "#000000",
    diameter = 20,
  }) => {
    const radius = diameter / 2;
    return (
      <svg width={diameter} height={diameter} className="inline-block">
        <circle
          cx={radius}
          cy={radius}
          r={radius - 1}
          fill={bgColor}
          stroke="#000000"
          strokeWidth="1"
        />
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="middle"
          fill={textColor}
          fontSize={isMobile ? 8 : 10}
          fontWeight="bold"
        >
          {safeString(text, "CircleBadge.text")}
        </text>
      </svg>
    );
  };

  const mobileScale = isMobile ? 0.8 : 1;
  const grossWeight =
    parseInt(
      safeString(data?.vehicle_gross_weight, "vehicle_gross_weight"),
      10,
    ) || 0;
  const noOfAxle = Math.ceil(grossWeight / 10200);

  // ✅ Safe rc_number for all slice/string operations
  const rcNumber = safeString(data?.rc_number, "rc_number");
  const stateCode = rcNumber.trim().slice(0, 2) || "XX";

  return (
    <div className="flex justify-center items-center w-full overflow-x-auto">
      <div
        className="flex flex-col md:flex-row justify-center gap-2 md:gap-5"
        style={{
          transform: `scale(${mobileScale})`,
          transformOrigin: "center top",
          width: isMobile ? "125%" : "100%",
        }}
      >
        {/* ========== FRONT CARD ========== */}
        <div
          className="relative w-[350px] h-[220px] bg-cover bg-center border border-black rounded p-1"
          style={{
            backgroundImage: `url(${compressedFront || Front})`,
            backgroundSize: "100% 100%",
          }}
        >
          <div className="absolute top-1.5 left-2">
            <img
              src={assets.ashok1 || logo}
              alt="Ashok Stambh"
              className="w-7 h-[50px] bg-transparent"
            />
          </div>

          <div className="absolute flex justify-between text-[10px] tracking-tight top-1 left-16 w-[78%] gap-3">
            <div>
              <h3 style={{ fontWeight: "800" }}>
                Indian Union Vehicle Registration Certificate
              </h3>
              <p style={{ fontWeight: "800" }}>
                Issued by Government of {stateName}
              </p>
            </div>
            <div className="flex gap-1 items-end w-15">
              <CircleBadge text="NT" bgColor="#7dd3fc" />
              <CircleBadge text={stateCode} bgColor="#fb923c" />
            </div>
          </div>

          {/* CHIP */}
          {includeChip && chipImage && (
            <div
              className="absolute"
              style={{
                top: "72px",
                left: "7px",
                width: "72px",
                height: "70px",
                backgroundImage: `url(${chipImage})`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
              }}
            />
          )}

          <div className="absolute top-11 left-20 w-[72%]">
            <div className="flex justify-between text-[8px] max-w-[120%] w-full tracking-tight">
              <div style={{ fontWeight: "800" }}>
                <p>Regn No</p>
                <p>{rcNumber}</p>
              </div>
              <div style={{ fontWeight: "800" }}>
                <p>Date of Regn.</p>
                <p>
                  {safeString(data?.registration_date, "registration_date")}
                </p>
              </div>
              <div style={{ fontWeight: "800" }}>
                <p>Regn. Validity</p>
                <p>{safeString(data?.fit_up_to, "fit_up_to")}</p>
              </div>
              <div className="flex flex-row justify-between">
                <div style={{ fontWeight: "800" }}>
                  <p>Owner</p>
                  <p>Serial</p>
                </div>
              </div>
              <div className="mt-2">
                <CircleBadge
                  text={safeString(data?.owner_number, "owner_number")}
                  bgColor="transparent"
                />
              </div>
            </div>

            <div className="flex flex-col justify-between gap-0 text-[8px] leading-snug w-[100%] tracking-tight">
              <div>
                <p style={{ fontWeight: "800" }}>Chassis No</p>
                <p className="font-semibold">
                  {safeString(
                    data?.vehicle_chasi_number,
                    "vehicle_chasi_number",
                  )}
                </p>
              </div>
              <div>
                <p style={{ fontWeight: "800" }}>Engine/Motor No</p>
                <p className="font-semibold">
                  {safeString(
                    data?.vehicle_engine_number,
                    "vehicle_engine_number",
                  )}
                </p>
              </div>
              <div>
                <p style={{ fontWeight: "800" }}>Owner Name</p>
                <p className="font-semibold">
                  {safeString(data?.owner_name, "owner_name")}
                </p>
              </div>
              <div className="w-full">
                <p style={{ fontWeight: "800" }}>
                  Son/Wife/Daughter of (In case of Individual Owner)
                </p>
                <p className="font-semibold absolute">
                  {safeString(data?.father_name, "father_name")}
                </p>
              </div>
              <div className="mt-2">
                <p style={{ fontWeight: "800" }}>Ownership</p>
                <p className="font-semibold">INDIVIDUAL</p>
              </div>
              <div className="gap-0.1 max-w-[75%]">
                <p style={{ fontWeight: "800" }}>Address</p>
                <p className="font-semibold">
                  {safeString(data?.present_address, "present_address")}
                </p>
              </div>
            </div>
          </div>

          <div className="absolute bottom-3 left-2 text-[8px] leading-snug w-[20%] tracking-tight">
            <div>
              <p style={{ fontWeight: "800" }}>Fuel</p>
              <p className="font-semibold">
                {safeString(data?.fuel_type, "fuel_type")}
              </p>
            </div>
            <div>
              <p style={{ fontWeight: "800" }}>Emission Norms</p>
              <p className="font-semibold">
                {safeString(data?.norms_type, "norms_type")}
              </p>
            </div>
          </div>

          <div className="relative">
            <div
              className={`text-right text-[10px] absolute ${
                rcNumber.length >= 9 ? "-right-[42px]" : "-right-[24px]"
              } top-[130px] -rotate-90 tracking-tighter`}
            >
              Card Issue Date:(
              {safeString(data?.permit_issue_date, "permit_issue_date") ||
                "2025-07-25"}
              )
            </div>
          </div>
        </div>

        {/* ========== BACK CARD ========== */}
        <div
          className="relative w-[350px] h-[220px] bg-cover bg-center border border-black rounded p-1"
          style={{
            backgroundImage: `url(${compressedBack || Back})`,
            backgroundSize: "100% 100%",
          }}
        >
          <div className="absolute flex justify-between text-[10px] top-3 left-2 w-[72.8%] tracking-tighter">
            <div className="flex gap-1 w-[20%]">
              <CircleBadge text="NT" bgColor="#7dd3fc" />
              <CircleBadge text={stateCode} bgColor="#fb923c" />
            </div>
            <div className="flex gap-1 text-[9px] mt-0 items-top justify-center">
              <p>
                <span style={{ fontWeight: "800" }}>Vehicle Class:</span>{" "}
                {safeString(
                  data?.vehicle_category_description,
                  "vehicle_category_description",
                )}
              </p>
            </div>
          </div>

          <div className="Flex flex-row text-[10px] tracking-tight">
            {/* Left */}
            <div className="absolute top-10 left-2 text-[8px] leading-snug w-[45%] tracking-tight">
              <div className="w-[25%]">
                <p style={{ fontWeight: "800" }}>Regn. No</p>
                <p className="mb-2" style={{ fontWeight: "800" }}>
                  {rcNumber}
                </p>
              </div>
              <div>
                <QRCode
                  value={qrDataText}
                  size={90}
                  level="Q"
                  fgColor="#000000"
                  bgColor="#ffffff"
                />
              </div>
              <div className="mt-2">
                <p style={{ fontWeight: "800" }}>Month-Year of Mfg.</p>
                <p className="font-semibold">
                  {safeString(data?.manufacturing_date, "manufacturing_date")}
                </p>
              </div>
              <div className="flex flex-row gap-2">
                <p style={{ fontWeight: "800" }}>No. of Cylinders</p>{" "}
                {safeString(data?.no_cylinders, "no_cylinders")}
              </div>
              <div className="flex flex-row gap-2">
                <p style={{ fontWeight: "800" }}>No of Axle</p> {noOfAxle}
              </div>
            </div>

            {/* Right */}
            <div className="absolute top-10 left-[110px] text-[8px] leading-tight w-[65%] tracking-tighter">
              <div className="leading-tight tracking-tighter">
                <p style={{ fontWeight: "800" }}>Maker's Name:</p>
                <p>
                  {safeString(data?.maker_description, "maker_description")}
                </p>
              </div>
              <div>
                <p style={{ fontWeight: "800" }}>Model Name:</p>
                <p>{safeString(data?.maker_model, "maker_model")}</p>
              </div>
              <div className="flex flex-row gap-2">
                <div>
                  <p style={{ fontWeight: "800" }}>Color:</p>
                  <p>{safeString(data?.color, "color")}</p>
                </div>
                <div>
                  <p style={{ fontWeight: "800" }}>/ Body Type:</p>
                  <p>/ {safeString(data?.body_type, "body_type")}</p>
                </div>
              </div>
              <div className="flex flex-row gap-2">
                <div>
                  <p style={{ fontWeight: "800" }}>Seating (in all)</p>
                  <p>{safeString(data?.seat_capacity, "seat_capacity")}</p>
                </div>
                <div>
                  <p style={{ fontWeight: "800" }}>/ Standing</p>
                  <p>
                    / {safeString(data?.standing_capacity, "standing_capacity")}
                  </p>
                </div>
                <div>
                  <p style={{ fontWeight: "800" }}>/ Sleeper Capacity</p>
                  <p>
                    / {safeString(data?.sleeper_capacity, "sleeper_capacity")}
                  </p>
                </div>
              </div>
              <div className="flex flex-row gap-2">
                <div>
                  <p style={{ fontWeight: "800" }}>Unladen</p>
                  <p>{safeString(data?.unladen_weight, "unladen_weight")}</p>
                </div>
                <div>
                  <p style={{ fontWeight: "800" }}>/ Laden</p>
                  <p>
                    /{" "}
                    {safeString(
                      data?.vehicle_gross_weight,
                      "vehicle_gross_weight",
                    )}
                  </p>
                </div>
                <div>
                  <p style={{ fontWeight: "800" }}>
                    / Gross Combination Weight (kg)
                  </p>
                  <p>
                    /{" "}
                    {safeString(
                      data?.gross_combination_weight,
                      "gross_combination_weight",
                    )}
                  </p>
                </div>
              </div>
              <div className="flex flex-row gap-2">
                <div>
                  <p style={{ fontWeight: "800" }}>Cubic Cap.</p>
                  <p>{safeString(data?.cubic_capacity, "cubic_capacity")}</p>
                </div>
                <div>
                  <p style={{ fontWeight: "800" }}>/Horse Power (BHP/Kw)</p>
                  <p>/ {safeString(data?.horse_power, "horse_power")}</p>
                </div>
                <div>
                  <p style={{ fontWeight: "800" }}>/Wheel base (mm)</p>
                  <p>/ {safeString(data?.wheelbase, "wheelbase")}</p>
                </div>
              </div>
              <div>
                <p style={{ fontWeight: "800" }}>Financier</p>
                <p>{safeString(data?.financer, "financer")}</p>
              </div>
            </div>

            <div className="absolute bottom-3 right-11 leading-snug w-[25%] tracking-tight">
              <div className="leading-tight text-[4px]">
                <p className="text-[7px] font-semibold tracking-tight leading-tight">
                  Registration Authority
                </p>
                <p className="font-semibold text-[6px]">
                  {safeString(data?.registered_at, "registered_at")}
                </p>
              </div>
            </div>

            <div className="text-right text-[9px] absolute right-0 top-[125px] -rotate-90 tracking-tighter">
              Form 23A
            </div>

            {/* SIGNATURE */}
            {includeSignature && signatureImage && (
              <div
                className="absolute"
                style={{
                  bottom: "25px",
                  right: "58px",
                  width: "60px",
                  height: "30px",
                  backgroundImage: `url(${signatureImage})`,
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OdishaRCCard;
